import type { AppDispatch } from '@redux/store'

import handleCallbackAction from '@functions/handleCallbackAction'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as customShortcodeActions from '@redux/modules/customShortcode'

import type { DeleteParams, EntityOptions } from '@redux/modules/types'
import type { CustomShortCodeModel } from '@domain-group/realhub-backend-api-types'

type CreateParams = {
  customShortcodeParams: Partial<CustomShortCodeModel>,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const createCustomShortcode = (params: CreateParams) => {
  const { customShortcodeParams, dispatch, requestOptions } = params
  const { createCustomShortcode: createFn } = customShortcodeActions

  return dispatch(createFn(customShortcodeParams, requestOptions))
}

type UpdateParams = {
  customShortcode: CustomShortCodeModel,
  customShortcodeParams: Partial<CustomShortCodeModel>,
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
}

const updateCustomShortcode = (params: UpdateParams) => {
  const {
    customShortcode, customShortcodeParams, dispatch, requestOptions,
  } = params
  const { updateCustomShortcode: updateFn } = customShortcodeActions

  const updatedParams = {
    id: customShortcode.id,
    ...customShortcodeParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteParamsType = {
  customShortcode: DeleteParams<CustomShortCodeModel>,
  dispatch: AppDispatch,
}

const deleteCustomShortcode = (params: DeleteParamsType) => {
  const { customShortcode, dispatch } = params
  const { deleteCustomShortcode: deleteFn } = customShortcodeActions

  return dispatch(deleteFn(customShortcode))
}

function useCustomShortcode() {
  const dispatch = useDispatch()

  const { creating, deleting, updating } = useSelector(reduxState => reduxState.customShortcodes)

  return {
    callbacks: {
      createCustomShortcode: (
        customShortcodeParams: Partial<CustomShortCodeModel>,
        requestOptions?: EntityOptions,
      ) => (
        handleCallbackAction({
          callbacks: {
            action: () => createCustomShortcode({ customShortcodeParams, dispatch, requestOptions }),
          },
          toastText: 'Custom Shortcode Created',
        })
      ),
      deleteCustomShortcode: (customShortcode: DeleteParams<CustomShortCodeModel>) => (
        handleCallbackAction({
          callbacks: {
            action: () => deleteCustomShortcode({ customShortcode, dispatch }),
          },
          toastText: 'Custom Shortcode Deleted',
        })
      ),
      updateCustomShortcode: (
        customShortcode: CustomShortCodeModel,
        customShortcodeParams: Partial<CustomShortCodeModel>,
        requestOptions?: EntityOptions,
      ) => (
        handleCallbackAction({
          callbacks: {
            action: () => updateCustomShortcode({
              customShortcode, customShortcodeParams, dispatch, requestOptions,
            }),
          },
          toastText: 'Custom Shortcode Updated',
        })
      ),
    },
    creating,
    deleting,
    updating,
  }
}

export default useCustomShortcode
